/**
 * AppUsers collection — admin-managed user records.
 *
 * Today this is the source of truth for what the Admin > Users
 * surface displays. At launch on Firebase, this collection moves to
 * Firestore (`users/{uid}`) and the role field is mirrored to Auth
 * custom claims via a Cloud Function (see implementationplan.md §7).
 *
 * The id-by-email handling here is a bridge: Firebase Auth assigns a
 * uid we don't control, so seeded users use `u-<slug>` ids that match
 * the mock provider. Once Firebase is wired, the seed script imports
 * via Auth admin SDK and stamps the real uid back here.
 */

import { db, nowIso } from './store';
import type { AppUser, AppUserRole, AppUserStatus } from './types';
import { seedAppUsers } from './seed';

export async function listAppUsers(): Promise<AppUser[]> {
  return db.list('appUsers');
}

export async function getAppUser(id: string): Promise<AppUser | null> {
  return db.get('appUsers', id);
}

export interface NewAppUserInput {
  email: string;
  displayName?: string;
  role: AppUserRole;
  notes?: string;
}

/**
 * Invite a user. Creates a record with `status: 'pending'` —
 * matching the Firebase invite flow where the row exists before the
 * user has accepted the email link. Throws if the email is already
 * present (we don't silently merge).
 *
 * The record is keyed by the LOWERCASED EMAIL (not a random id). This is
 * load-bearing for go-live: on first real sign-in, provisionUserClient
 * (AuthProvider) looks up `appUsers/{email}` to adopt the invited role
 * into the new uid-keyed record, and firestore.rules independently
 * verifies the adopted role against this same doc — which is only
 * possible because the rules can address it by the signer's own email.
 */
export async function inviteAppUser(input: NewAppUserInput): Promise<AppUser> {
  const all = await db.list('appUsers');
  const existing = all.find(
    (u) => u.email.toLowerCase() === input.email.toLowerCase(),
  );
  if (existing) {
    throw new Error(`A user with email ${input.email} already exists.`);
  }
  const ts = nowIso();
  const record: AppUser = {
    id: input.email.trim().toLowerCase(),
    email: input.email.trim(),
    displayName:
      (input.displayName ?? '').trim() || input.email.split('@')[0],
    role: input.role,
    status: 'pending',
    notes: input.notes,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('appUsers', record);
  return record;
}

export async function updateAppUser(
  id: string,
  patch: Partial<Omit<AppUser, 'id' | 'createdAt'>>,
): Promise<AppUser> {
  return db.update('appUsers', id, { ...patch, updatedAt: nowIso() });
}

/** Set a user's role. Mirrors the Cloud Function that sets custom claims. */
export async function setAppUserRole(
  id: string,
  role: AppUserRole,
): Promise<AppUser> {
  return updateAppUser(id, { role });
}

/** Mark a user as active / disabled. Disabled users can't sign in. */
export async function setAppUserStatus(
  id: string,
  status: AppUserStatus,
): Promise<AppUser> {
  return updateAppUser(id, { status });
}

/** Stamp a sign-in. Called by AuthProvider on successful sign-in. */
export async function recordSignIn(email: string): Promise<void> {
  const all = await db.list('appUsers');
  const match = all.find(
    (u) => u.email.toLowerCase() === email.toLowerCase(),
  );
  if (!match) return;
  await db.update('appUsers', match.id, { lastSignInAt: nowIso() });
}

export async function deleteAppUser(id: string): Promise<void> {
  await db.remove('appUsers', id);
}

export function subscribeAppUsers(fn: (users: AppUser[]) => void): () => void {
  return db.subscribe('appUsers', fn);
}

export async function bootstrapAppUsersIfEmpty(): Promise<void> {
  const existing = await db.list('appUsers');
  if (existing.length > 0) return;
  await db.replaceAll('appUsers', seedAppUsers);
}
